<template lang="html">
  <div class="project-list">
    <!--================= Dashboard Map =================-->
    <mq-layout mq="lg+">
      <section class="map-section visible-dashboard">
        <projects-map
          :projects="currentProjects"
          @project-selected="projectSelected"
        ></projects-map>
      </section>
    </mq-layout>

    <!--================= Mobile Map =================-->
    <mq-layout mq="sm">
      <section v-if="!error && displayMap" class="map-section fill-screen">
        <projects-map
          :projects="currentProjects"
          @project-selected="projectSelected"
        ></projects-map>
      </section>
    </mq-layout>

    <!--================= Projects List =================-->
    <section
      v-show="(!error && !displayMap) || (!error && $mq !== 'sm')"
      class="section"
    >
      <h1 class="title is-4 has-text-centered hidden-dashboard">Projects</h1>
      <div class="has-text-centered">
        <span v-if="loading" class="icon has-text-primary is-large">
          <i class="las la-2x la-sync fa-spin"></i>
        </span>
      </div>
      <h4
        v-if="displayFavoriteProjects"
        class="title is-size-4 has-text-grey-medium project-section-title"
      >
        Favorites
      </h4>
      <cw-project-list
        v-if="displayFavoriteProjects"
        :projects="favoriteProjects"
        :selectionHandler="projectSelected"
        :cellIconProps="{
          name: 'pin',
          scale: 2,
          colorClass: 'has-text-primary',
        }"
      />
      <h4
        v-if="displayFavoriteProjects"
        class="title is-size-4 has-text-grey-medium project-section-title"
      >
        All
      </h4>
      <cw-project-list
        v-if="currentProjects && currentProjects.length > 0"
        :projects="currentProjects"
        :selectionHandler="projectSelected"
      />
    </section>

    <!--================= Error indicator =================-->
    <section v-if="!displayMap" class="section">
      <div class="has-text-centered">
        <div v-if="error" class="centerit">
          <div class="wifi-loader is-primary"></div>
        </div>
        <div class="title is-size-5 font-light">
          <p v-if="error" class="is-padded-medium">
            There has been a network error!
          </p>
          <p v-else-if="!loading && (!projects || projects.length === 0)">
            No Projects found
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import store from '@/store';
import { mapState } from 'vuex';
import {
  PROJECTS_QUERY,
  PROJECTS_FOR_LOCATION_QUERY,
  CUSTOMER_FAMILY_QUERY,
} from '@/graphql/constants';
import authRequired from '@/components/mixins/auth-required';
import autoRefresh from '@/components/mixins/auto-refresh';
import locationAware from '@/components/mixins/location-aware';

import ProjectsMap from '@/components/blocks/ProjectsMap';

// FOR SCREENSHOTS
import Mock from '@/mock-data/screenshots';
import screenshotConfig from '@/components/mixins/screenshot-config';

export default {
  name: 'projects',
  // share common functionality with component mixins
  mixins: [authRequired, autoRefresh, screenshotConfig, locationAware],
  props: {},
  components: {
    'projects-map': ProjectsMap,
  },
  // variables
  data() {
    return {
      error: undefined,
      projects: null,
      projectsForLocation: null,
      loadingProjects: 0,
      loadingLocationProjects: 0,
      selectedProject: null,
      useLocationSpecificProjects: false,
      bypassLocationFiltering: false, // Set to false to enable location filtering
      loadingCustomer: 0,
    };
  },
  created() {
    // Set useLocationSpecificProjects based on hasMultipleLocations
    this.useLocationSpecificProjects = this.hasMultipleLocations;

    // Subscribe to Vuex store changes for location
    this.$store.subscribe(mutation => {
      if (mutation.type === 'locations/setSelectedCustomerId') {
        console.log('Location changed via store mutation:', mutation.payload);
        this.handleLocationChange(mutation.payload);
      }
    });

    // Listen for the location-changed event from HeaderLocationDisplay
    this.$root.$on('location-changed', customerId => {
      console.log(
        'Location changed event received with customerId:',
        customerId
      );
      this.handleLocationChange(customerId);
    });

    // Make sure location data is initialized for multi-location setups
    if (this.fetchCustomerFamily) {
      this.fetchCustomerFamily().then(() => {
        // Check if we have multiple locations
        if (this.customerFamily && this.customerFamily.length > 1) {
          // Enable location filtering if multiple locations exist
          this.useLocationSpecificProjects = true;

          // Ensure we have a selected location
          if (!this.selectedCustomerId && this.customerFamily.length > 0) {
            this.selectLocation(this.customerFamily[0].customerId);
          }
        }

        // Force refresh the appropriate query based on location status
        if (this.useLocationSpecificProjects && this.selectedCustomerId) {
          if (this.$apollo.queries.projectsForLocation) {
            this.$apollo.queries.projectsForLocation.refetch();
          }
        } else {
          if (this.$apollo.queries.projects) {
            this.$apollo.queries.projects.refetch();
          }
        }
      });
    }
  },
  apollo: {
    projects: {
      query: PROJECTS_QUERY,
      loadingKey: 'loadingProjects',
      result() {
        this.error = null;
      },
      // FOR SCREENSHOTS
      update(data) {
        if (this.capturingScreenshots) {
          return Mock.projects;
        }
        return data && data.projects ? data.projects : [];
      },
      error(err) {
        this.error = err;
      },
    },
    projectsForLocation: {
      query: PROJECTS_FOR_LOCATION_QUERY,
      loadingKey: 'loadingLocationProjects',
      variables() {
        return {
          customerId: this.selectedCustomerId,
        };
      },
      skip() {
        // Only skip if we don't have multiple locations or no customer ID selected
        return !this.hasMultipleLocations || !this.selectedCustomerId;
      },
      result() {
        this.error = null;
      },
      update(data) {
        if (this.capturingScreenshots) {
          return Mock.projects;
        }
        return data && data.projectsForLocation ? data.projectsForLocation : [];
      },
      error(err) {
        this.error = err;
      },
    },
    customerFamily: {
      query: CUSTOMER_FAMILY_QUERY,
      update(data) {
        return data.customerFamily || [];
      },
      result({ data }) {
        if (data && data.customerFamily) {
          if (data.customerFamily.length > 1) {
            this.useLocationSpecificProjects = true;

            // Set global flag for multiple locations
            if (this.$root.$data) {
              this.$root.$data.multipleLocations = true;
            }

            // If no location is selected yet, use the first one
            if (!this.selectedCustomerId && data.customerFamily.length > 0) {
              console.log(
                'Setting initial customer ID to',
                data.customerFamily[0].customerId
              );
              this.selectLocation(data.customerFamily[0].customerId);
            }
          }
        }
      },
      error(err) {
        console.error('Error fetching customer family:', err);
      },
    },
  },
  computed: {
    currentProjects() {
      // Use location-specific projects if available, otherwise fall back to regular projects
      // This is the key computed property that determines which projects to display
      // We determine which set of projects to use based on multiple locations flag
      const useLocationSpecific =
        this.hasMultipleLocations && Boolean(this.selectedCustomerId);

      console.log(
        'Loading projects with locationSpecific:',
        useLocationSpecific,
        'selectedCustomerId:',
        this.selectedCustomerId,
        'hasMultipleLocations:',
        this.hasMultipleLocations,
        'projectsForLocation:',
        this.projectsForLocation ? this.projectsForLocation.length : 0,
        'projects:',
        this.projects ? this.projects.length : 0
      );

      // First check if we have location-specific projects
      if (useLocationSpecific && this.projectsForLocation) {
        console.log(
          'Using location-specific projects for location:',
          this.selectedCustomerId
        );
        const filtered = this.projectsForLocation.filter(e => !e.archived);
        return filtered;
      }

      // Otherwise fall back to regular projects
      if (this.projects) {
        console.log('Using regular projects (no location filtering)');
        return this.projects.filter(e => !e.archived);
      }

      return [];
    },
    favoriteProjects() {
      let projects = this.currentProjects;
      if (projects && this.user.prefs) {
        projects = projects.filter(
          e => this.user.prefs.favoriteProjects[e.projectId]
        );
      } else {
        projects = [];
      }
      return projects;
    },
    displayFavoriteProjects() {
      return (
        this.user &&
        this.user.prefs &&
        this.user.prefs.favoriteProjects &&
        Object.keys(this.user.prefs.favoriteProjects).length > 0
      );
    },
    loading() {
      return (
        this.loadingProjects +
          this.loadingLocationProjects +
          this.loadingCustomer >
        0
      );
    },
    ...mapState({
      displayMap: state => state.projects.displayMap,
      user: state => state.user,
    }),
  },
  // methods
  watch: {
    userInfo() {
      this.$apollo.queries.projects.refetch();
    },
    hasMultipleLocations(newValue) {
      console.log('hasMultipleLocations changed to:', newValue);
      if (newValue) {
        this.useLocationSpecificProjects = true;

        // If we have a selectedCustomerId, refetch location-specific projects
        if (
          this.selectedCustomerId &&
          this.$apollo.queries.projectsForLocation
        ) {
          this.$apollo.queries.projectsForLocation.refetch();
        }
      } else {
        this.useLocationSpecificProjects = false;

        // Refetch standard projects
        if (this.$apollo.queries.projects) {
          this.$apollo.queries.projects.refetch();
        }
      }
    },
    selectedCustomerId(newId, oldId) {
      if (newId !== oldId) {
        console.log('Customer ID changed from', oldId, 'to', newId);
        this.handleLocationChange(newId);
      }
    },
  },
  methods: {
    handleLocationChange(newCustomerId) {
      // Force refetch of location-specific projects
      if (this.useLocationSpecificProjects && newCustomerId) {
        console.log('Refetching projectsForLocation...');

        // We need to enable projectsForLocation if it was skipped
        if (this.$apollo.queries.projectsForLocation) {
          // If the query exists, just refetch it
          this.$apollo.queries.projectsForLocation.refetch();
        } else {
          // If the query doesn't exist yet, we need to restart it
          this.$apollo.addSmartQuery(
            'projectsForLocation',
            this.$options.apollo.projectsForLocation
          );
        }
      }
    },

    projectSelected(project) {
      this.$router.push({ path: `/projects/${project.projectId}` });
    },

    // Method to manually refresh projects based on location status
    refreshProjects() {
      if (
        this.hasMultipleLocations &&
        this.useLocationSpecificProjects &&
        this.selectedCustomerId
      ) {
        console.log('Refreshing location-specific projects');
        if (this.$apollo.queries.projectsForLocation) {
          this.$apollo.queries.projectsForLocation.refetch();
        } else {
          // If the query doesn't exist yet, we need to restart it
          this.$apollo.addSmartQuery(
            'projectsForLocation',
            this.$options.apollo.projectsForLocation
          );
        }
      } else {
        console.log('Refreshing standard projects');
        if (this.$apollo.queries.projects) {
          this.$apollo.queries.projects.refetch();
        }
      }
    },

    // Fallback method in case we need to manually set a location
    initializeLocation() {
      if (
        this.hasMultipleLocations &&
        !this.selectedCustomerId &&
        this.customerFamily &&
        this.customerFamily.length > 0
      ) {
        this.selectLocation(this.customerFamily[0].customerId);
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    store.commit('header/setDropdownActions', {
      actions: [
        {
          title: () => (store.state.projects.displayMap ? 'List' : 'Map'),
          icon: () =>
            store.state.projects.displayMap ? 'fa-th-list' : 'fa-map',
          action: () => {
            store.commit('projects/toggleMap');
          },
        },
      ],
    });
    next();
  },
  beforeRouteLeave: (to, from, next) => {
    store.commit('header/setDropdownActions', {
      actions: [],
    });
    // remove this line to restore map when route is pushed on
    store.commit('projects/hideMap');
    next();
  },
  beforeDestroy() {
    // Remove event listener to prevent memory leaks
    this.$root.$off('location-changed');
  },
};
</script>

<style lang="stylus">



.map-section.visible-dashboard {
  width: 100%
  height: 20em
  border-radius: 10px
  overflow: hidden
  border: 1px solid #ddd
  margin-bottom: 1em
}

.map-section.fill-screen {
  position: absolute
  top: 0
  right: 0
  bottom: 0
  left: 0
}

.project-section-title {
  padding-top: 2rem;
  padding-bottom: 0;
}
</style>
